import React, { useEffect, useRef, useState } from "react";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import {
  reactToSocialPost,
  toggleSaveSocialPost,
  updateSocialPost,
  deleteSocialPost,
} from "./communitySocialApi";
import {
  trackAdClick,
  trackAdImpression,
} from "../Advertising/advertisingApi";
import DisplayAd from "../Advertising/DisplayAd";
import {
  isDisplayNetworkItem,
  selectDisplayUnit,
} from "../Advertising/providers/AdProviderResolver";
import { PostBodyWithEmbeds } from "./youtubeEmbed";
import CommunityPostMedia from "./CommunityPostMedia";
import PrayerTypeDialog from "./PrayerTypeDialog";
import ReactionTypeDialog from "./ReactionTypeDialog";
import CommunityPostHeader from "./CommunityPostHeader";
import CommunityPostComments from "./CommunityPostComments";

function ReactionSummaryBar({ summary }) {
  const icons = (summary && summary.icons) || [];
  const total = summary && summary.total ? Number(summary.total) : 0;
  if (!icons.length || total < 1) {
    return null;
  }
  return (
    <div className="community-post__reaction-summary" aria-label={`${total} reactions`}>
      <span className="community-post__reaction-stack">
        {icons.map((icon, idx) => {
          const img = icon.image;
          const key = `${icon.kind || "r"}-${icon.id || idx}`;
          return (
            <span
              key={key}
              className="community-post__reaction-icon"
              style={{ zIndex: icons.length - idx }}
              title={icon.name || ""}
            >
              {img ? (
                <img src={resolveMediaUrl(img)} alt="" onError={handleMediaImgError} />
              ) : (
                <span aria-hidden="true">{icon.emoji || "👍"}</span>
              )}
            </span>
          );
        })}
      </span>
      <span className="community-post__reaction-total">{total}</span>
    </div>
  );
}

function unitSize(unit) {
  const width = unit ? parseInt(unit.width, 10) : 0;
  const height = unit ? parseInt(unit.height, 10) : 0;
  return { width: width > 0 ? width : 0, height: height > 0 ? height : 0 };
}

function networkAdUnits(network) {
  return {
    desktop: unitSize(selectDisplayUnit(network, { device: "desktop" })),
    mobile: unitSize(selectDisplayUnit(network, { device: "mobile" })),
  };
}

/**
 * Feed ad presentation size:
 *  (A) nominal unit W×H — the unit DisplayAd selects per device class;
 *  (B) ad host width — measured by useNetworkAdScale (container, not viewport);
 *  (C) NETWORK_AD_MAX_UPSCALE — how far past W a creative may grow.
 * Presentation width = min(host width, W × cap); height follows the unit ratio.
 * 1.5: provider creatives are 1x bitmaps (the CANB tag offers no srcset / 2x
 * assets) and soften visibly past ~1.5x; 300×250 → 450×375 reads as a large
 * rectangle without dominating the post. Short banners (320×100) gain little
 * height, and the host width always bounds them on narrow columns.
 */
const NETWORK_AD_MAX_UPSCALE = 1.5;

/**
 * Nominal unit size and the upscale cap, exposed as CSS custom properties so
 * the feed ad slot caps at W × cap, shrinks below it, and derives creative
 * iframe height from the unit ratio.
 */
function networkAdSizeStyle(network) {
  const style = { "--feed-ad-max-upscale": String(NETWORK_AD_MAX_UPSCALE) };
  const { desktop, mobile } = networkAdUnits(network);
  if (desktop.width > 0) style["--feed-ad-max-width"] = desktop.width + "px";
  if (mobile.width > 0) style["--feed-ad-max-width-mobile"] = mobile.width + "px";
  if (desktop.width > 0 && desktop.height > 0) {
    style["--feed-ad-ratio"] = desktop.width + " / " + desktop.height;
  }
  if (mobile.width > 0 && mobile.height > 0) {
    style["--feed-ad-ratio-mobile"] = mobile.width + " / " + mobile.height;
  }
  return style;
}

/**
 * Fixed-size provider iframe documents cannot reflow, so the creative keeps its
 * native W×H and is transform-scaled uniformly to the presentation width:
 * min(host / W, NETWORK_AD_MAX_UPSCALE), matching the slot cap. CSS sizes the
 * provider box to the scaled (fit) size. Transforms apply to iframe content in
 * every engine; `zoom` does not in WebKit. The host is a full-width block, so
 * its width never depends on the scaled child. Measured per device-class unit;
 * CSS picks the active one.
 */
function useNetworkAdScale(hostRef, desktopWidth, desktopHeight, mobileWidth, mobileHeight) {
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
      return undefined;
    }
    const setVar = (prop, value) => {
      if (host.style.getPropertyValue(prop) !== value) host.style.setProperty(prop, value);
    };
    const sync = () => {
      const width = host.clientWidth;
      [
        ["", desktopWidth, desktopHeight],
        ["-mobile", mobileWidth, mobileHeight],
      ].forEach(([suffix, unitWidth, unitHeight]) => {
        if (width > 0 && unitWidth > 0 && unitHeight > 0) {
          const scale = Math.min(NETWORK_AD_MAX_UPSCALE, width / unitWidth).toFixed(4);
          const factor = Number(scale);
          setVar("--feed-ad-scale" + suffix, scale);
          setVar("--feed-ad-width" + suffix, unitWidth + "px");
          setVar("--feed-ad-height" + suffix, unitHeight + "px");
          setVar("--feed-ad-fit-width" + suffix, Math.floor(unitWidth * factor * 100) / 100 + "px");
          setVar("--feed-ad-fit-height" + suffix, Math.floor(unitHeight * factor * 100) / 100 + "px");
        }
      });
    };
    const observer = new window.ResizeObserver(sync);
    observer.observe(host);
    return () => observer.disconnect();
  }, [hostRef, desktopWidth, desktopHeight, mobileWidth, mobileHeight]);
}

/**
 * Shared Community post card — header, body, media, reactions, prayer, actions.
 * Consumed by ActivityFeed (My Feed, Profile, entity feeds).
 */
function CommunityPostCard({
  item,
  onReacted = null,
  onDeleted = null,
  showRankingMeta = true,
}) {
  const author = item.author || {};
  const displayName = author.display_name || author.name || item.title || "Member";
  const handle = author.handle;
  const avatar = author.avatar || item.logo_path || item.avatar;
  const cover = author.cover || author.community_cover || null;
  const authorPath = author.path || null;
  const body = item.body || item.content || item.summary || item.description || "";
  const counts = item.counts || {};
  const viewerReactions = item.viewer_reactions || [];
  const timestamp = item.timestamp || item.created_at || "";
  const isPlaceholder = !!item._placeholder || !!item._optimistic;
  const isOwner = !!item.is_owner;
  const hasLike =
    viewerReactions.indexOf("like") >= 0 ||
    item.viewer_reaction_type_id != null;
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(body);
  const [busy, setBusy] = useState(false);
  const [prayOpen, setPrayOpen] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(
    counts.comment != null ? Number(counts.comment) : 0
  );
  const recommendation = item.recommendation || null;
  const recommendationLabel =
    (recommendation && recommendation.label) || item.recommendation_label || null;
  const sponsoredLabel =
    item.sponsored_label ||
    (item.is_sponsored ? "Sponsored" : item.is_house ? "Featured" : null);
  // Sponsored / house must always be visibly labeled — never gated on ranking meta chrome.
  const showSponsored =
    !!sponsoredLabel &&
    (!!item.is_sponsored || item.kind === "sponsored" || !!item.is_house);
  const isNativeAd = !!item.is_native_ad || !!item.is_placeholder_sponsored;
  const networkAd =
    item.network && isDisplayNetworkItem(item.network) ? item.network : null;
  const isNetworkAd = !!networkAd || !!item.is_network_ad;
  const sponsoredCta =
    !isNetworkAd && item.sponsored_cta && item.sponsored_cta.url
      ? item.sponsored_cta
      : null;
  const adCampaignId = item.ad_campaign_id || null;
  const adCreativeId = item.ad_creative_id || null;
  const adPlacementKey = item.ad_placement_key || "community_feed";
  const impressionRef = useRef(null);
  const networkAdRef = useRef(null);
  const networkAdSizes = networkAd ? networkAdUnits(networkAd) : null;
  useNetworkAdScale(
    networkAdRef,
    networkAdSizes ? networkAdSizes.desktop.width : 0,
    networkAdSizes ? networkAdSizes.desktop.height : 0,
    networkAdSizes ? networkAdSizes.mobile.width : 0,
    networkAdSizes ? networkAdSizes.mobile.height : 0
  );
  // Avoid "Featured" + "Featured for you" (or any duplicate meta) on the same card.
  const showRec =
    showRankingMeta &&
    !!recommendationLabel &&
    !showSponsored &&
    recommendationLabel !== sponsoredLabel &&
    item.feed_bucket !== "personal";

  useEffect(() => {
    if (!showSponsored || !adCampaignId || item.is_placeholder_sponsored) {
      return undefined;
    }
    if (impressionRef.current) {
      return undefined;
    }
    impressionRef.current = "pending";
    let cancelled = false;
    trackAdImpression({
      campaign_id: adCampaignId,
      creative_id: adCreativeId,
      placement_key: adPlacementKey,
    }).then((res) => {
      if (!cancelled && res && res.impression_id) {
        impressionRef.current = res.impression_id;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    showSponsored,
    adCampaignId,
    adCreativeId,
    adPlacementKey,
    item.is_placeholder_sponsored,
  ]);

  const handleSponsoredCtaClick = async (event) => {
    if (!sponsoredCta || !sponsoredCta.url) {
      if (event) event.preventDefault();
      return;
    }
    if (adCampaignId) {
      await trackAdClick({
        campaign_id: adCampaignId,
        creative_id: adCreativeId,
        placement_key: adPlacementKey,
        impression_id:
          impressionRef.current && impressionRef.current !== "pending"
            ? impressionRef.current
            : null,
      });
    }
  };

  const runReaction = async (reaction, prayerTypeId = null, reactionTypeId = null) => {
    if (isPlaceholder || typeof item.id !== "number") {
      if (typeof onReacted === "function") {
        const next = { ...item };
        const set = new Set(viewerReactions);
        const countKey = reaction === "prayer" || reaction === "amen" ? "prayer" : "like";
        if (prayerTypeId != null) {
          set.add("prayer");
          next.viewer_prayer_type_id = prayerTypeId;
          if (viewerReactions.indexOf("prayer") < 0 && viewerReactions.indexOf("amen") < 0) {
            next.counts = {
              ...counts,
              prayer: (counts.prayer || 0) + 1,
            };
          }
        } else if (reactionTypeId != null) {
          set.add("like");
          next.viewer_reaction_type_id = reactionTypeId;
          if (viewerReactions.indexOf("like") < 0) {
            next.counts = {
              ...counts,
              like: (counts.like || 0) + 1,
            };
          }
        } else if (set.has(reaction)) {
          set.delete(reaction);
          next.counts = {
            ...counts,
            [countKey]: Math.max(0, (counts[countKey] || 0) - 1),
          };
          if (reaction === "prayer") next.viewer_prayer_type_id = null;
          if (reaction === "like") next.viewer_reaction_type_id = null;
        } else {
          set.add(reaction);
          next.counts = {
            ...counts,
            [countKey]: (counts[countKey] || 0) + 1,
          };
        }
        next.viewer_reactions = Array.from(set);
        onReacted(next);
      }
      return;
    }
    const result = await reactToSocialPost(
      item.id,
      reaction,
      prayerTypeId,
      reactionTypeId
    );
    if (!result.error && result.post && typeof onReacted === "function") {
      onReacted(result.post);
    }
  };

  const runSave = async () => {
    if (isPlaceholder || typeof item.id !== "number") {
      return;
    }
    const result = await toggleSaveSocialPost(item.id);
    if (!result.error && result.post && typeof onReacted === "function") {
      onReacted(result.post);
    } else if (!result.error && typeof onReacted === "function") {
      onReacted({ ...item, is_saved: !!result.saved });
    }
  };

  const runEditSave = async () => {
    if (isPlaceholder || typeof item.id !== "number" || busy) return;
    const next = (editBody || "").trim();
    if (!next && !(item.media && item.media.path)) return;
    setBusy(true);
    const result = await updateSocialPost(item.id, next);
    setBusy(false);
    if (!result.error && result.post) {
      setEditing(false);
      if (typeof onReacted === "function") onReacted(result.post);
    }
  };

  const runDelete = async () => {
    if (isPlaceholder || typeof item.id !== "number" || busy) return;
    if (typeof window !== "undefined" && !window.confirm("Delete this post?")) {
      return;
    }
    setBusy(true);
    const result = await deleteSocialPost(item.id);
    setBusy(false);
    if (!result.error) {
      if (typeof onDeleted === "function") onDeleted(item.id);
      else if (typeof onReacted === "function") onReacted({ ...item, _deleted: true });
    }
  };

  return (
    <article
      className={
        "community-feed-item community-post activity-feed__post" +
        (cover ? " community-post--has-cover" : "") +
        (item.is_prayer_request ? " community-post--prayer-request" : "") +
        (showSponsored ? " community-post--sponsored community-feed-item--ad" : "") +
        (item.feed_bucket ? ` community-post--bucket-${item.feed_bucket}` : "")
      }
    >
      {showSponsored || showRec ? (
        <div className="community-post__meta-row">
          {showSponsored ? (
            <span className="community-post__badge community-post__badge--sponsored">
              {sponsoredLabel}
            </span>
          ) : null}
          {showRec ? (
            <span className="community-post__badge community-post__badge--recommend">
              {recommendationLabel}
            </span>
          ) : null}
          {showRankingMeta && item.feed_bucket === "personal" ? (
            <span className="community-post__badge community-post__badge--personal">
              Your activity
            </span>
          ) : null}
        </div>
      ) : showRankingMeta && item.feed_bucket === "personal" ? (
        <div className="community-post__meta-row">
          <span className="community-post__badge community-post__badge--personal">
            Your activity
          </span>
        </div>
      ) : null}

      {!networkAd ? (
        <CommunityPostHeader
          item={item}
          displayName={displayName}
          handle={handle}
          avatar={avatar}
          cover={cover}
          authorPath={authorPath}
          timestamp={timestamp}
          createdAt={item.created_at || null}
        />
      ) : null}

      {item.is_prayer_request ? (
        <p className="community-post__prayer-badge" role="status">
          Prayer Request
        </p>
      ) : null}

      {editing ? (
        <div className="community-post__edit">
          <textarea
            className="community-post__edit-textarea"
            rows={3}
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            disabled={busy}
          />
          <div className="community-post__edit-actions">
            <button
              type="button"
              className="community-post__action"
              onClick={runEditSave}
              disabled={busy}
            >
              Save
            </button>
            <button
              type="button"
              className="community-post__action"
              onClick={() => {
                setEditing(false);
                setEditBody(body);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : body ? (
        <PostBodyWithEmbeds
          text={body}
          className="community-post__body"
          linkPreview={item.link_preview || null}
        />
      ) : null}

      {networkAd ? (
        <div
          className="community-post__network-ad"
          style={networkAdSizeStyle(networkAd)}
          ref={networkAdRef}
        >
          <DisplayAd
            network={networkAd}
            placementKey={adPlacementKey}
            className="community-post__display-ad"
          />
        </div>
      ) : (
        <CommunityPostMedia item={item} />
      )}

      {showSponsored && sponsoredCta ? (
        <div className="community-post__sponsored-cta">
          <a
            className="community-post__sponsored-cta-link"
            href={sponsoredCta.url}
            target={
              String(sponsoredCta.url).indexOf("http") === 0
                ? "_blank"
                : undefined
            }
            rel={
              String(sponsoredCta.url).indexOf("http") === 0
                ? "noopener noreferrer sponsored"
                : undefined
            }
            onClick={handleSponsoredCtaClick}
          >
            {sponsoredCta.label || "Learn more"}
          </a>
        </div>
      ) : null}

      <ReactionSummaryBar summary={item.reaction_summary} />

      {!isNativeAd && !item.is_placeholder_sponsored ? (
      <footer className="community-post__actions">
        <button
          type="button"
          className={
            "community-post__action" +
            (viewerReactions.indexOf("like") >= 0 ? " is-active" : "")
          }
          onClick={() => setReactOpen(true)}
          onContextMenu={(e) => {
            e.preventDefault();
            setReactOpen(true);
          }}
          disabled={isPlaceholder}
        >
          Like{counts.like ? ` · ${counts.like}` : ""}
        </button>
        <button
          type="button"
          className={
            "community-post__action" + (commentsOpen ? " is-active" : "")
          }
          onClick={() => setCommentsOpen((open) => !open)}
          disabled={isPlaceholder}
        >
          Comment{commentCount ? ` · ${commentCount}` : ""}
        </button>
        <button
          type="button"
          className={
            "community-post__action" + (item.is_saved ? " is-active" : "")
          }
          onClick={runSave}
          disabled={isPlaceholder}
          title={item.is_saved ? "Unsave post" : "Save post"}
        >
          {item.is_saved ? "Saved" : "Save"}
        </button>
        <button
          type="button"
          className={
            "community-post__action community-post__action--prayer" +
            (viewerReactions.indexOf("prayer") >= 0 ||
            viewerReactions.indexOf("amen") >= 0
              ? " is-active"
              : "")
          }
          onClick={() => setPrayOpen(true)}
          disabled={isPlaceholder}
        >
          Pray{counts.prayer ? ` · ${counts.prayer}` : ""}
        </button>
        {isOwner && !isPlaceholder ? (
          <>
            <button
              type="button"
              className="community-post__action"
              onClick={() => {
                setEditBody(body);
                setEditing(true);
              }}
              disabled={busy || editing}
            >
              Edit
            </button>
            <button
              type="button"
              className="community-post__action community-post__action--danger"
              onClick={runDelete}
              disabled={busy}
            >
              Delete
            </button>
          </>
        ) : null}
      </footer>
      ) : null}
      {commentsOpen && !isPlaceholder && !isNativeAd && item.id ? (
        <CommunityPostComments
          postId={item.id}
          commentsLocked={!!item.comments_locked}
          initialCount={commentCount}
          onCountChange={(n) => {
            const next = Number(n) || 0;
            setCommentCount(next);
            if (typeof onReacted === "function") {
              onReacted({
                ...item,
                counts: { ...counts, comment: next },
              });
            }
          }}
        />
      ) : null}
      <ReactionTypeDialog
        open={reactOpen}
        busy={busy}
        activeTypeId={item.viewer_reaction_type_id || null}
        hasActiveReaction={hasLike}
        onClose={() => setReactOpen(false)}
        onSelect={async (type) => {
          setBusy(true);
          if (!type) {
            await runReaction("like");
          } else {
            await runReaction("like", null, type.id);
          }
          setBusy(false);
          setReactOpen(false);
        }}
      />
      <PrayerTypeDialog
        open={prayOpen}
        busy={busy}
        activeTypeId={item.viewer_prayer_type_id || null}
        onClose={() => setPrayOpen(false)}
        onSelect={async (type) => {
          setBusy(true);
          if (!type) {
            await runReaction("prayer");
          } else {
            await runReaction("prayer", type.id);
          }
          setBusy(false);
          setPrayOpen(false);
        }}
      />
    </article>
  );
}

export default CommunityPostCard;
